import * as React from 'react';
import Button from 'components/Button';
import { useTranslation } from 'next-i18next';
import Link from 'components/Link';
import api from 'api/api';
import Paragraph from 'components/text/Paragraph';
import Heading from 'components/text/Heading';
import { Form, RHFInput } from 'components/form/RHF';
import { defaultValues, forgotPasswordValidationSchema } from './ForgotPasswordForm.schema';
import AuthContainer from '../AuthContainer';
import { routes } from 'utils/routes';

const ForgotPasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const [requestedEmail, setRequestedEmail] = React.useState('');

  const handleSubmit = async ({ email }: { email: string }) => {
    try {
      await api.user.mobileUser.requestPasswordReset(email);
      setRequestedEmail(email);
    } catch (e: any) {
      if (e.status === 'NOT_FOUND') {
        setRequestedEmail(email);
      } else {
        throw e;
      }
    }
  };

  const renderFormContent = () => {
    return (
      <>
        <AuthContainer.Header>
          <Heading variant="h4">{t('general.form.forgotPassword.title')}</Heading>
          <Paragraph className="mt-2">{t('general.form.forgotPassword.description')}</Paragraph>
        </AuthContainer.Header>
        <AuthContainer.Content>
          <Form
            initialValues={defaultValues}
            onSubmit={handleSubmit}
            validationSchema={forgotPasswordValidationSchema(t)}
            buttonLabel="general.form.forgotPassword.sendResetLink"
          >
            <RHFInput enable1Password required name="email" label="general.form.email" showLabel />
          </Form>
        </AuthContainer.Content>
        <AuthContainer.Footer className="flex justify-center">
          <Link href={routes.auth.login()} legacyBehavior passHref replace>
            <Button
              className="!text-primary hover:!text-primary-lighter self-center text-caption underline"
              variant="link"
            >
              {t('general.form.forgotPassword.backToLogin')}
            </Button>
          </Link>
        </AuthContainer.Footer>
      </>
    );
  };

  const renderSuccess = () => {
    return (
      <AuthContainer.Header className="flex flex-col items-center text-center">
        <Heading variant="h4">{t('general.form.forgotPassword.successMessage.title')}</Heading>
        <Paragraph>
          {t('general.form.forgotPassword.successMessage.description', {
            email: requestedEmail
          })}
        </Paragraph>
        <Link href={routes.auth.login()} legacyBehavior passHref replace>
          <Button
            className="!text-primary hover:!text-primary-lighter self-center text-caption underline"
            variant="link"
          >
            {t('general.form.forgotPassword.backToLogin')}
          </Button>
        </Link>
      </AuthContainer.Header>
    );
  };

  return (
    <AuthContainer.Root>
      {requestedEmail ? renderSuccess() : renderFormContent()}
    </AuthContainer.Root>
  );
};

export default ForgotPasswordForm;
